import { COLORS, getMonday, weekKey } from './config.js';

// ══════════════════════════════════════════════════════════
// STATE — Shared mutable state object
// All modules import { state } and mutate properties directly.
// ══════════════════════════════════════════════════════════
export const state = {
  currentMonday:        getMonday(new Date()),
  focusDay:             (new Date().getDay() + 6) % 7, // 0=Dl … 6=Dg, dia visible a la vista Dia
  db:                   {},            // { [weekKey]: Block[] }
  templates:            [],            // Template[]
  exceptions:           {},            // { "templateId|YYYY-MM-DD": true }
  blockTypes:           [],            // CustomBlockType[]
  tasks:                [],            // Task[] (local inbox)
  tags:                 [],            // Tag[] { id, name, color }
  calendarEvents:       [],            // CalendarEvent[] (timed)
  allDayEvents:         [],            // CalendarEvent[] (all-day)
  allGoogleCalendars:   [],            // { id, summary, backgroundColor, primary }[]
  selectedCalendarIds:  ['primary'],
  currentUser:          null,          // { id, name }

  // Modal state
  editingId:            null,
  pickedColor:          COLORS[0].id,
  colorManuallyPicked:  false,
  pickedTipus:          '',
  pickedEnergia:        '',
  recurEnabled:         false,
  pickedDays:           new Set(),
  pendingTaskId:        null,

  // Drag state
  drag:                 null,
  pending:              null,
  draw:                 null,

  // Task drag (sidebar → calendar)
  pendingTaskDrop:      null,

  // Sidebar selection
  selectedBlockId:      null,
  selectedTaskId:       null,
  selectedTipusId:      null,
  // Collapsed state of sidebar type sections (by tipus id)
  collapsedSections:    {},
  // Sidebar mode: 'active' (inbox + per-type) | 'archive' (completed tasks). Not persisted.
  sidebarMode:          'active',

  // GCal event modal
  editingGCalEvent:     null,
  pickedGCalColorId:    null,

  // Time tracking
  runningEntry:         null,          // open time entry { id, task_id, started_at, task_title… } or null
  timeEntries:          [],            // entries loaded for the dashboard range
};

// ── State mutation helpers ──────────────────────────────
export function getWeekBlocks() {
  return state.db[weekKey(state.currentMonday)] || [];
}

export function setWeekBlocks(arr) {
  state.db[weekKey(state.currentMonday)] = arr;
}

export function findBlockEntry(id) {
  for (const wk in state.db) {
    const i = state.db[wk].findIndex(x => x.id === id);
    if (i !== -1) return { wk, i, block: state.db[wk][i] };
  }
  return null;
}

export function findBlock(id) {
  const entry = findBlockEntry(id);
  return entry ? entry.block : null;
}

export function patchBlock(id, patch) {
  const entry = findBlockEntry(id);
  if (!entry) return;
  state.db[entry.wk][entry.i] = { ...entry.block, ...patch };
  return state.db[entry.wk][entry.i];
}

export function removeBlock(id) {
  const entry = findBlockEntry(id);
  if (entry) state.db[entry.wk].splice(entry.i, 1);
}

// ── Task helpers ──────────────────────────────────────
export function findTask(id) {
  return state.tasks.find(t => t.id === id) || null;
}

export function patchTask(id, patch) {
  const i = state.tasks.findIndex(t => t.id === id);
  if (i === -1) return null;
  state.tasks[i] = { ...state.tasks[i], ...patch };
  return state.tasks[i];
}

export function removeTask(id) {
  const i = state.tasks.findIndex(t => t.id === id);
  if (i !== -1) state.tasks.splice(i, 1);
}

// ── Subtasks (one level: a subtask has parent_id) ─────
export function subtasksOf(id) {
  return state.tasks.filter(t => t.parent_id === id);
}

export function isTopLevel(task) {
  return !task?.parent_id;
}

// ── Task tracked time ─────────────────────────────────
// Closed intervals only, in ms (tracked_seconds comes from GET /api/tasks).
export function taskClosedMs(task) {
  return Math.max(0, Number(task?.tracked_seconds) || 0) * 1000;
}

// Total time of a task in ms: closed intervals + the live session if this task is the one running.
export function taskTrackedMs(task) {
  const base = taskClosedMs(task);
  const r = state.runningEntry;
  if (!task || !r || r.task_id !== task.id) return base;
  const started = new Date(r.started_at).getTime();
  if (isNaN(started)) return base;
  return base + Math.max(0, Date.now() - started);
}

// Same totals for a task plus its subtasks (what the parent shows as "temps total").
export function taskTreeClosedMs(task) {
  return subtasksOf(task?.id).reduce((sum, t) => sum + taskClosedMs(t), taskClosedMs(task));
}

export function taskTreeTrackedMs(task) {
  return subtasksOf(task?.id).reduce((sum, t) => sum + taskTrackedMs(t), taskTrackedMs(task));
}

// True when the running timer belongs to this task or to one of its subtasks.
export function isTreeRunning(task) {
  const r = state.runningEntry;
  if (!task || !r) return false;
  return r.task_id === task.id || subtasksOf(task.id).some(t => t.id === r.task_id);
}
